import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import PlaceSearch from './components/common/PlaceSearch';
import Favorites from './components/Favorites';
import { FavoritesProvider, useFavorites } from './context/FavoritesContext';
import BeeSVG from './components/common/BeeSVG';
import SplashScreen from './components/common/SplashScreen';
import OfflineBanner from './components/common/OfflineBanner';
import InstallPrompt from './components/common/InstallPrompt';

const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('beezy-dark-mode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('beezy-dark-mode', String(isDark));
  }, [isDark]);

  const toggle = useCallback(() => setIsDark((prev) => !prev), []);

  return [isDark, toggle];
};

const DarkModeToggle = ({ isDark, onToggle }) => (
  <button
    onClick={onToggle}
    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full glass transition-all duration-300 hover:scale-110 active:scale-95"
    aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    title={isDark ? 'Light mode' : 'Dark mode'}
  >
    {isDark ? (
      <svg className="w-5 h-5 text-honey-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ) : (
      <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    )}
  </button>
);

DarkModeToggle.propTypes = {
  isDark: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const TabBar = ({ activeTab, setActiveTab }) => {
  const { favorites } = useFavorites();
  const searchRef = useRef(null);
  const favRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});

  useEffect(() => {
    const activeRef = activeTab === 'search' ? searchRef : favRef;
    if (activeRef.current) {
      const { offsetLeft, offsetWidth } = activeRef.current;
      setIndicatorStyle({
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  }, [activeTab]);

  return (
    <nav className="relative flex justify-center gap-2 mb-8">
      <div className="relative flex glass rounded-full p-1.5">
        <div
          className="absolute top-1.5 h-[calc(100%-12px)] bg-honey-500 rounded-full transition-all duration-300 ease-out shadow-glow-honey"
          style={indicatorStyle}
        />
        <button
          ref={searchRef}
          onClick={() => setActiveTab('search')}
          className={`relative z-10 px-6 py-2.5 min-h-[44px] rounded-full text-sm font-medium transition-colors duration-300 ${
            activeTab === 'search'
              ? 'text-white'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          Search Places
        </button>
        <button
          ref={favRef}
          onClick={() => setActiveTab('favorites')}
          className={`relative z-10 px-6 py-2.5 min-h-[44px] rounded-full text-sm font-medium transition-colors duration-300 flex items-center gap-2 ${
            activeTab === 'favorites'
              ? 'text-white'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          My Favorites
          {favorites.length > 0 && (
            <span
              className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full transition-colors duration-300 ${
                activeTab === 'favorites'
                  ? 'bg-white/30 text-white'
                  : 'bg-honey-100 text-honey-700 dark:bg-honey-900/50 dark:text-honey-400'
              }`}
            >
              {favorites.length}
            </span>
          )}
        </button>
      </div>
    </nav>
  );
};

TabBar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
};

function AppContent() {
  const [activeTab, setActiveTab] = useState('search');
  const [isDark, toggleDark] = useDarkMode();
  const [showSplash, setShowSplash] = useState(true);

  return (
    <>
      {showSplash && <SplashScreen onReady={() => setShowSplash(false)} />}
      <OfflineBanner />
      <InstallPrompt />
      <div className="min-h-screen bg-gradient-to-br from-honey-50 via-white to-honey-100/50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 transition-colors duration-300">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <header className="text-center mb-10 relative">
            <div className="absolute right-0 top-0">
              <DarkModeToggle isDark={isDark} onToggle={toggleDark} />
            </div>
            <div className="flex items-center justify-center gap-3 mb-3">
              <BeeSVG />
              <h1 className="text-3xl sm:text-4xl font-bold honey-text-gradient">Beezy</h1>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-light tracking-wide">
              Real-time occupancy for your favorite places
            </p>
          </header>

          {/* Tab Navigation */}
          <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Main Content */}
          <main className="glass-card p-6 md:p-8 mb-8">
            <div key={activeTab} className="tab-content-enter">
              {activeTab === 'search' ? (
                <PlaceSearch onFavoriteAdded={() => setActiveTab('favorites')} />
              ) : (
                <Favorites />
              )}
            </div>
          </main>

          {/* Footer */}
          <footer className="text-center text-gray-400 dark:text-gray-500 text-xs font-light mt-12 pb-4 pb-safe">
            <p>Beezy &middot; Find the perfect moment</p>
          </footer>
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <FavoritesProvider>
      <AppContent />
    </FavoritesProvider>
  );
}

export default App;
